export const TEXTAREA_TAB_INDENT = '  ';

export interface TextareaTabIndentationInput {
  value: string;
  selectionStart: number;
  selectionEnd: number;
  shiftKey?: boolean;
  indent?: string;
}

export interface TextareaTabIndentationResult {
  value: string;
  selectionStart: number;
  selectionEnd: number;
}

function getLineStart(value: string, index: number): number {
  const safeIndex = Math.max(0, Math.min(index, value.length));
  const previousNewline = value.lastIndexOf('\n', Math.max(0, safeIndex - 1));
  return previousNewline === -1 ? 0 : previousNewline + 1;
}

function getLineEnd(value: string, index: number): number {
  const safeIndex = Math.max(0, Math.min(index, value.length));
  const nextNewline = value.indexOf('\n', safeIndex);
  return nextNewline === -1 ? value.length : nextNewline;
}

function getEffectiveSelectionEnd(value: string, selectionStart: number, selectionEnd: number): number {
  if (selectionEnd <= selectionStart) return selectionEnd;
  return value[selectionEnd - 1] === '\n' ? selectionEnd - 1 : selectionEnd;
}

function getMultilineSelectionRange(value: string, selectionStart: number, selectionEnd: number): { start: number; end: number } {
  const start = getLineStart(value, selectionStart);
  const effectiveEnd = getEffectiveSelectionEnd(value, selectionStart, selectionEnd);
  const end = getLineEnd(value, effectiveEnd);
  return { start, end };
}

function countLeadingSpaces(line: string): number {
  const match = line.match(/^ +/);
  return match ? match[0].length : 0;
}

function getRemovedIndentLength(line: string, indent: string): number {
  if (!line) return 0;
  if (line.startsWith(indent)) return indent.length;
  if (line.startsWith('\t')) return 1;

  const leadingSpaces = countLeadingSpaces(line);
  return leadingSpaces > 0 ? Math.min(indent.length, leadingSpaces) : 0;
}

function countAffectedLines(value: string, start: number, end: number): number {
  if (end <= start) return 1;
  return value.slice(start, end).split('\n').length;
}

function countRemovedCharactersBeforeOffset(
  value: string,
  start: number,
  end: number,
  removedPerLine: number[],
  offset: number,
): number {
  let totalRemoved = 0;
  let lineIndex = 0;
  let cursor = start;

  while (cursor <= end && lineIndex < removedPerLine.length) {
    const lineEnd = cursor + (value.slice(cursor, end).split('\n', 1)[0]?.length ?? 0);
    if (offset < cursor) {
      break;
    }

    const removedHere = removedPerLine[lineIndex] ?? 0;
    if (offset <= lineEnd) {
      totalRemoved += Math.min(removedHere, Math.max(0, offset - cursor));
      break;
    }

    totalRemoved += removedHere;
    if (lineEnd >= end) {
      break;
    }

    cursor = lineEnd + 1;
    lineIndex += 1;
  }

  return totalRemoved;
}

export function applyTextareaTabIndentation({
  value,
  selectionStart,
  selectionEnd,
  shiftKey = false,
  indent = TEXTAREA_TAB_INDENT,
}: TextareaTabIndentationInput): TextareaTabIndentationResult {
  const hasSelection = selectionStart !== selectionEnd;
  const selectedText = value.slice(selectionStart, selectionEnd);
  const isMultilineSelection = hasSelection && selectedText.includes('\n');

  if (!shiftKey) {
    if (!isMultilineSelection) {
      const nextValue = `${value.slice(0, selectionStart)}${indent}${value.slice(selectionStart)}`;
      const nextSelectionStart = selectionStart + indent.length;
      const nextSelectionEnd = hasSelection ? selectionEnd + indent.length : nextSelectionStart;

      return {
        value: nextValue,
        selectionStart: nextSelectionStart,
        selectionEnd: nextSelectionEnd,
      };
    }

    const range = getMultilineSelectionRange(value, selectionStart, selectionEnd);
    const block = value.slice(range.start, range.end);
    const lines = block.split('\n');
    const indentedBlock = lines.map((line) => `${indent}${line}`).join('\n');
    const affectedLines = countAffectedLines(value, range.start, range.end);

    return {
      value: `${value.slice(0, range.start)}${indentedBlock}${value.slice(range.end)}`,
      selectionStart: selectionStart + indent.length,
      selectionEnd: selectionEnd + indent.length * affectedLines,
    };
  }

  if (!isMultilineSelection) {
    const lineStart = getLineStart(value, selectionStart);
    const lineEnd = getLineEnd(value, selectionEnd);
    const line = value.slice(lineStart, lineEnd);
    const removed = getRemovedIndentLength(line, indent);

    if (removed === 0) {
      return { value, selectionStart, selectionEnd };
    }

    return {
      value: `${value.slice(0, lineStart)}${line.slice(removed)}${value.slice(lineEnd)}`,
      selectionStart: Math.max(lineStart, selectionStart - removed),
      selectionEnd: Math.max(lineStart, selectionEnd - removed),
    };
  }

  const range = getMultilineSelectionRange(value, selectionStart, selectionEnd);
  const block = value.slice(range.start, range.end);
  const lines = block.split('\n');
  const removedPerLine = lines.map((line) => getRemovedIndentLength(line, indent));
  const unindentedBlock = lines.map((line, index) => line.slice(removedPerLine[index] ?? 0)).join('\n');

  const removedBeforeStart = countRemovedCharactersBeforeOffset(
    block,
    0,
    block.length,
    removedPerLine,
    selectionStart - range.start,
  );
  const removedBeforeEnd = countRemovedCharactersBeforeOffset(
    block,
    0,
    block.length,
    removedPerLine,
    selectionEnd - range.start,
  );

  return {
    value: `${value.slice(0, range.start)}${unindentedBlock}${value.slice(range.end)}`,
    selectionStart: Math.max(range.start, selectionStart - removedBeforeStart),
    selectionEnd: Math.max(range.start, selectionEnd - removedBeforeEnd),
  };
}
